import type {
  CustomerDocument,
  NewCustomerDocumentInput,
} from '../domain/models'
import type {
  AuthStorePort,
  ClockPort,
  CustomerDocumentsRepositoryPort,
  IdGeneratorPort,
} from '../ports'

/**
 * Files kept on the customer's record — the Documents tab.
 *
 * The commands mirror the customer-notes trio: an add that hands back the
 * write in flight (so a failed upload can be rolled back before it leaves a
 * tombstone), a soft delete for what users remove, and a hard discard
 * reserved for that rollback.
 */

export function addCustomerDocumentWithTransaction(
  customerId: string,
  input: NewCustomerDocumentInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    customerDocumentsRepo: CustomerDocumentsRepositoryPort
  },
): {
  document: CustomerDocument
  transaction: ReturnType<CustomerDocumentsRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  const document: CustomerDocument = {
    id: deps.idGenerator.generateId(),
    customerId,
    createdAt: timestamp,
    updatedAt: timestamp,
    ...input,
    // Forced last: the uploader is who is signed in, never what the caller says.
    uploadedBy: currentUser.id,
  }

  const transaction = deps.customerDocumentsRepo.insert(document)

  return { document, transaction }
}

/**
 * Soft-delete a document: it drops off the tab but the file stays stored,
 * matching how notes are removed.
 */
export function deleteCustomerDocument(
  customerId: string,
  documentId: string,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    customerDocumentsRepo: CustomerDocumentsRepositoryPort
  },
) {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  deps.customerDocumentsRepo.update(documentId, (draft) => {
    if (draft.customerId !== customerId) return
    if (draft.deletedAt) return
    draft.deletedAt = timestamp
    draft.deletedBy = currentUser.id
    draft.updatedAt = timestamp
  })
}

/**
 * Hard-delete a document.
 *
 * Reserved for rolling back a document whose upload failed — it never became
 * part of the record, so it should leave nothing behind. Everything a user
 * deletes goes through {@link deleteCustomerDocument} instead.
 */
export function discardCustomerDocument(
  documentId: string,
  deps: { customerDocumentsRepo: CustomerDocumentsRepositoryPort },
) {
  deps.customerDocumentsRepo.delete(documentId)
}
